import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { DialogService } from 'src/app/services/dialog.service';
import { FormulaTesterComponent } from 'src/app/forms/formula-tester/formula-tester.component';
import { DataSourceConfig, FormulaDataSource, MockDataSource, ApiDataSource, Variable } from '../../interfaces/data-source.models';
import { DataSourceExecutorService } from '../../services/data-source-executor.service';
import { NzMessageService } from 'ng-zorro-antd/message';
import { LanguageServices } from 'src/app/services/LanguageServices';

/**
 * DataSourceConfigComponent
 * 
 * Componente para configurar fonte de dados de um launcher.
 * 
 * Tipos disponíveis:
 * - Formula: Abre FormulaTesterComponent (REUTILIZA componente existente)
 * - Mock: Editor JSON inline
 * - API: Configuração de request HTTP inline
 * 
 * Este componente DELEGA a configuração de fórmulas para o FormulaTesterComponent
 * existente através do DialogService.
 */
@Component({
  selector: 'app-data-source-config',
  templateUrl: './data-source-config.component.html',
  styleUrls: ['./data-source-config.component.scss'],
  standalone: false
})
export class DataSourceConfigComponent implements OnInit {
  @Input() visible = false;
  @Input() config: DataSourceConfig = { type: 'none' };
  @Output() visibleChange = new EventEmitter<boolean>();
  @Output() configChange = new EventEmitter<DataSourceConfig>();

  selectedType: 'none' | 'formula' | 'mock' | 'api' = 'none';
  
  // Configs temporárias para cada tipo
  formulaConfig: FormulaDataSource = { 
    type: 'formula', 
    expression: '', 
    variables: [],
    description: ''
  };
  
  mockConfig: MockDataSource = { 
    type: 'mock', 
    jsonData: '{\n  "items": []\n}',
    description: ''
  };
  
  apiConfig: ApiDataSource = { 
    type: 'api', 
    url: '', 
    method: 'GET',
    headers: {},
    body: '',
    description: ''
  };

  testResult: { success: boolean; message: string; data?: string } | null = null;
  testing = false;

  constructor(
    private readonly ls: LanguageServices,
    private dialogService: DialogService,  // ✅ REUTILIZA DialogService
    private dataSourceExecutor: DataSourceExecutorService,
    private message: NzMessageService
  ) {}

  ngOnInit() {
    // Carrega configuração existente se fornecida
    if (this.config && this.config.type !== 'none') {
      this.selectedType = this.config.type;
      
      if (this.config.type === 'formula') {
        this.formulaConfig = { ...this.config };
      } else if (this.config.type === 'mock') {
        this.mockConfig = { ...this.config };
      } else if (this.config.type === 'api') {
        this.apiConfig = { ...this.config };
      }
    }
  }

  /**
   * Abre o editor de fórmulas (FormulaTesterComponent)
   * 
   * ✅ REUTILIZA FormulaTesterComponent existente
   * Usa o mesmo padrão do json-config.component.ts linha 389-398
   */
  openFormulaEditor(): void {
    const variables: Variable[] = this.formulaConfig.variables || [];
    
    this.dialogService.openGenericDialog(
      FormulaTesterComponent,
      { 
        formula: this.formulaConfig.expression, 
        variables: variables 
      },
      (result: any) => {
        if (result && result.formula) {
          this.formulaConfig.expression = result.formula;
          if (result.variables) {
            this.formulaConfig.variables = result.variables;
          }
          this.message.success(this.ls.instant('builderScreen.msg.dataSource.formulaConfigured'));
        }
      },
      () => {},
      '80vh',
      '80vw',
      false,
      null,
      'S',
      '80px'
    );
  }

  /**
   * Testa a configuração atual do data source
   */
  testCurrentConfig(): void {
    let configToTest: DataSourceConfig;

    switch (this.selectedType) {
      case 'formula':
        if (!this.formulaConfig.expression) {
          this.message.warning(this.ls.instant('builderScreen.msg.dataSource.formulaBeforeTest'));
          return;
        }
        configToTest = this.formulaConfig;
        break;
      case 'mock':
        configToTest = this.mockConfig;
        break;
      case 'api':
        if (!this.apiConfig.url) {
          this.message.warning(this.ls.instant('builderScreen.msg.dataSource.urlBeforeTest'));
          return;
        }
        configToTest = this.apiConfig;
        break;
      default:
        this.message.warning(this.ls.instant('builderScreen.msg.dataSource.typeRequired'));
        return;
    }

    this.testResult = null;
    this.testing = true;

    this.dataSourceExecutor.execute(configToTest).subscribe({
      next: (data) => {
        this.testing = false;
        this.testResult = {
          success: true,
          message: 'Teste executado com sucesso!',
          data: JSON.stringify(data, null, 2)
        };
        this.message.success(this.ls.instant('builderScreen.msg.dataSource.testSuccess'));
      },
      error: (error) => {
        this.testing = false;
        this.testResult = {
          success: false,
          message: 'Erro ao executar teste',
          data: error.message || error.toString()
        };
        this.message.error(this.ls.instant('builderScreen.msg.dataSource.testError'));
      }
    });
  }

  /**
   * Valida o JSON do mock
   */
  validateMockJson(): void {
    try {
      JSON.parse(this.mockConfig.jsonData);
      this.message.success(this.ls.instant('builderScreen.msg.json.valid'));
    } catch (error) {
      this.message.error(this.ls.instant('builderScreen.msg.json.invalidDetail', { error: error.message }));
    }
  }

  /**
   * Adiciona um header à configuração de API
   */
  addHeader(): void {
    if (!this.apiConfig.headers) {
      this.apiConfig.headers = {};
    }
    // Placeholder para novo header
    this.apiConfig.headers[''] = '';
  }

  /**
   * Remove um header da configuração de API
   */
  removeHeader(key: string): void {
    if (this.apiConfig.headers) {
      delete this.apiConfig.headers[key];
    }
  }

  /**
   * Retorna as chaves dos headers como array
   */
  getHeaderKeys(): string[] {
    return this.apiConfig.headers ? Object.keys(this.apiConfig.headers) : [];
  }

  /**
   * Atualiza chave de um header
   */
  updateHeaderKey(oldKey: string, newKey: string): void {
    if (this.apiConfig.headers && oldKey !== newKey) {
      const value = this.apiConfig.headers[oldKey];
      delete this.apiConfig.headers[oldKey];
      this.apiConfig.headers[newKey] = value;
    }
  }

  /**
   * Confirma e salva a configuração
   */
  handleOk(): void {
    let finalConfig: DataSourceConfig;

    switch (this.selectedType) {
      case 'formula':
        if (!this.formulaConfig.expression) {
          this.message.error(this.ls.instant('builderScreen.msg.dataSource.formulaBeforeSave'));
          return;
        }
        finalConfig = this.formulaConfig;
        break;
        
      case 'mock':
        try {
          JSON.parse(this.mockConfig.jsonData);
          finalConfig = this.mockConfig;
        } catch (error) {
          this.message.error(this.ls.instant('builderScreen.msg.json.fixBeforeSave'));
          return;
        }
        break;
        
      case 'api':
        if (!this.apiConfig.url) {
          this.message.error(this.ls.instant('builderScreen.msg.dataSource.urlBeforeSave'));
          return;
        }
        finalConfig = this.apiConfig;
        break;
        
      default:
        finalConfig = { type: 'none' };
    }

    this.configChange.emit(finalConfig);
    this.visible = false;
    this.visibleChange.emit(false);
    this.message.success(this.ls.instant('builderScreen.msg.dataSource.saved'));
  }

  /**
   * Cancela e fecha o modal
   */
  handleCancel(): void {
    this.visible = false;
    this.visibleChange.emit(false);
  }

  /**
   * Retorna label descritiva do tipo selecionado
   */
  getTypeLabel(): string {
    switch (this.selectedType) {
      case 'formula': return 'Fórmula';
      case 'mock': return 'Mock JSON';
      case 'api': return 'API';
      default: return 'Nenhum';
    }
  }
}

